import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingApi } from '../services/api';
import { PageHead, Badge, RefCode, AreaPill, Btn, TableWrap, Th, Td, Empty, Spinner, Toast, Modal, Card, Textarea } from '../components/UI';
import { getLastNDaysRange, parseYmd } from '../utils/dateRange';

const STATUS_FILTERS = ['All', 'Pending', 'Approved', 'Rejected', 'Cancelled'];

export default function BookingsPage() {
  const navigate = useNavigate();
  const initialRange = getLastNDaysRange(6);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [areaFilter, setAreaFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [toast, setToast] = useState(null);
  const [cancelModal, setCancelModal] = useState(null); // booking object
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await bookingApi.getMy(undefined, undefined, undefined);
      setBookings(res.data.data || []);
    } catch {
      setBookings(DEMO);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await bookingApi.cancel(cancelModal.bookingID || cancelModal.id, cancelReason);
      setToast({ msg: 'Booking LE cancelled successfully.', type: 'success' });
      setCancelModal(null);
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Failed to cancel Booking LE.', type: 'error' });
    } finally {
      setCancelling(false);
    }
  }

  function inDateRange(item) {
    const raw = item?.submittedAt || item?.eventDate || item?.date;
    const itemDate = parseYmd(raw);
    if (!itemDate) return false;

    const start = parseYmd(fromDate);
    const end = parseYmd(toDate);
    if (start && itemDate < start) return false;
    if (end && itemDate > end) return false;
    return true;
  }

  const areaOptions = Array.from(
    new Set(
      bookings
        .map((b) => b.eventAreaName || b.area)
        .filter(Boolean)
    )
  ).sort();

  const displayed = bookings
    .filter((b) => filter === 'All' || b.status === filter)
    .filter((b) => areaFilter === 'All' || (b.eventAreaName || b.area) === areaFilter)
    .filter((b) => inDateRange(b))
    .filter((b) => {
      const q = search.toLowerCase();
      return !q
        || (b.eventName || b.event || '').toLowerCase().includes(q)
        || (b.bookingReference || b.ref || '').toLowerCase().includes(q)
        || (b.eventAreaName || b.area || '').toLowerCase().includes(q);
    });

  return (
    <div className="page-anim">
      <PageHead
        title="Booking LE"
        subtitle="Use filters to find your bookings quickly"
        actions={<Btn variant="primary" onClick={() => navigate('/new-booking')}>+ Booking LE</Btn>}
      />

      {/* Filters */}
      <Card style={{ marginBottom: '1rem', padding: '1rem 1.2rem' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_FILTERS.map((s) => (
            <Btn
              key={s}
              size="sm"
              variant={filter === s ? 'primary' : 'ghost'}
              onClick={() => setFilter(s)}
            >
              {s}
            </Btn>
          ))}
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            style={{ padding: '7px 12px', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', outline: 'none', background: 'white' }}
          >
            <option value="All">All Areas</option>
            {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: '7px 10px', fontSize: 13, minWidth: 140, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: '7px 10px', fontSize: 13, minWidth: 140, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event or reference..."
            style={{ padding: '7px 12px', fontSize: 13, flex: 1, minWidth: 140, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <Btn
            size="sm"
            variant="ghost"
            onClick={() => {
              const range = getLastNDaysRange(6);
              setFilter('All');
              setAreaFilter('All');
              setSearch('');
              setFromDate(range.from);
              setToDate(range.to);
            }}
          >
            Reset
          </Btn>
        </div>
      </Card>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', gap: 12 }}>
          <Spinner size={24} />
          <span style={{ color: 'var(--text2)' }}>Loading...</span>
        </div>
      ) : displayed.length === 0 ? (
        <Empty icon="[]" text="No Booking LE found" />
      ) : (
        <>
          <div className="bookings-table-wrap">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Reference</Th>
                  <Th>Event Name</Th>
                  <Th>Area</Th>
                  <Th>Date &amp; Time</Th>
                  <Th>Slots</Th>
                  <Th>Status</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((b) => (
                  <tr key={b.bookingID || b.id} style={{ cursor: 'pointer' }}>
                    <Td><RefCode value={b.bookingReference || b.ref} /></Td>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{b.eventName || b.event}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{b.campusName || b.campus} | {b.expectedAttendees || b.att || 0} attendees</div>
                    </Td>
                    <Td><AreaPill value={(b.eventAreaName || b.area || '').replace('Multi-Purpose Hall', 'MPH')} /></Td>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{b.eventDate || b.date}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{b.startTime || b.s} - {b.endTime || b.e}</div>
                    </Td>
                    <Td style={{ textAlign: 'center' }}>
                      <span style={{ background: 'var(--bg2)', padding: '3px 8px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{b.totalSlots || b.slots}</span>
                    </Td>
                    <Td><Badge status={b.status} /></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <Btn size="sm" onClick={() => navigate(`/bookings/${b.bookingID || b.id}`)}>View</Btn>
                        {b.status === 'Pending' && (
                          <Btn size="sm" variant="red" onClick={() => { setCancelModal(b); setCancelReason(''); }}>Cancel</Btn>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </div>

          <div className="bookings-mobile-list" style={{ display: 'none', flexDirection: 'column', gap: 10 }}>
            {displayed.map((b) => (
              <Card key={`mobile-${b.bookingID || b.id}`} style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <RefCode value={b.bookingReference || b.ref} />
                  <Badge status={b.status} />
                </div>
                <div style={{ marginTop: 8, fontWeight: 700, fontSize: 14 }}>{b.eventName || b.event}</div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>
                  {(b.eventAreaName || b.area || '').replace('Multi-Purpose Hall', 'MPH')}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>
                  {b.eventDate || b.date} | {b.startTime || b.s} - {b.endTime || b.e}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>
                  {(b.totalSlots || b.slots)} slot(s) | {b.expectedAttendees || b.att || 0} attendees
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Btn size="sm" onClick={() => navigate(`/bookings/${b.bookingID || b.id}`)}>View</Btn>
                  {b.status === 'Pending' && (
                    <Btn size="sm" variant="red" onClick={() => { setCancelModal(b); setCancelReason(''); }}>Cancel</Btn>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <Modal title="Cancel Booking LE" onClose={() => setCancelModal(null)}>
          <div style={{ padding: '12px 14px', background: 'var(--orange-light)', borderRadius: 8, marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{cancelModal.eventName || cancelModal.event}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{cancelModal.eventDate || cancelModal.date} | {cancelModal.eventAreaName || cancelModal.area}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1.2rem' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase' }}>Cancellation Reason</label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Optional: reason for cancellation..." speechToText style={{ minHeight: 140 }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="red" onClick={handleCancel} disabled={cancelling} style={{ flex: 1, justifyContent: 'center' }}>
              {cancelling ? <Spinner size={16} color="var(--red)" /> : 'Confirm Cancel'}
            </Btn>
            <Btn variant="ghost" onClick={() => setCancelModal(null)}>Keep Booking LE</Btn>
          </div>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <style>{`
        @media(max-width:768px){
          .bookings-table-wrap{display:none!important}
          .bookings-mobile-list{display:flex!important}
        }
      `}</style>
    </div>
  );
}

const DEMO = [
  { id: 1, bookingReference: 'CHW-20260601-0001', eventName: 'Annual Sports Day', eventAreaName: 'Multi-Purpose Hall', campusName: 'Chinchwad', eventDate: '2026-06-10', startTime: '08:00', endTime: '11:00', totalSlots: 3, status: 'Approved', expectedAttendees: 300 },
  { id: 2, bookingReference: 'CHW-20260601-0002', eventName: 'Science Exhibition', eventAreaName: 'Conference Room A', campusName: 'Chinchwad', eventDate: '2026-06-12', startTime: '09:00', endTime: '12:00', totalSlots: 3, status: 'Pending', expectedAttendees: 80 },
  { id: 3, bookingReference: 'HJW-20260601-0001', eventName: 'Parent-Teacher Meet', eventAreaName: 'Seminar Hall', campusName: 'Hinjawadi', eventDate: '2026-06-15', startTime: '14:00', endTime: '17:00', totalSlots: 3, status: 'Pending', expectedAttendees: 120 },
  { id: 4, bookingReference: 'CHW-20260601-0003', eventName: 'Robotics Workshop', eventAreaName: 'Computer Lab', campusName: 'Chinchwad', eventDate: '2026-06-18', startTime: '10:00', endTime: '13:00', totalSlots: 3, status: 'Approved', expectedAttendees: 40 },
  { id: 5, bookingReference: 'CHW-20260601-0004', eventName: 'Staff Meeting', eventAreaName: 'Conference Room B', campusName: 'Chinchwad', eventDate: '2026-06-20', startTime: '15:00', endTime: '16:00', totalSlots: 1, status: 'Rejected', expectedAttendees: 20 },
  { id: 6, bookingReference: 'HJW-20260601-0002', eventName: 'Cultural Programme', eventAreaName: 'Multi-Purpose Hall', campusName: 'Hinjawadi', eventDate: '2026-06-25', startTime: '16:00', endTime: '20:00', totalSlots: 4, status: 'Pending', expectedAttendees: 350 },
];
